import React, { useState, useEffect } from 'react';
import { Settings, Edit, Save, X, Plus, Trash2, Eye } from 'lucide-react';
import { collection, getDocs, doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';

/**
 * Composant pour gérer les templates de dashboard par rôle
 * Accessible uniquement aux administrateurs
 */
const DashboardTemplateManager = ({ db, currentUser }) => {
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  // Toutes les tuiles disponibles
  const allTiles = {
    dateTime: "Date & Heure",
    monEspace: "Mon espace",
    vueEnsemble: "Vue d'ensemble",
    prochainRendezVous: "Prochains rendez-vous",
    gestionCollaborateurs: "Gestion des collaborateurs",
    suiviPresences: "Présences",
    sommairePaie: "Sommaire paie",
    anniversaires: "Anniversaires à venir",
    suiviDocuments: "Documents",
    suiviTaches: "Tâches",
    suiviFactures: "Factures",
    suiviDevis: "Devis",
    suiviContacts: "Contacts"
  };

  const [availableRoles, setAvailableRoles] = useState([]);

  useEffect(() => {
    loadTemplates();
    loadAvailableRoles();
  }, [db]);

  const loadAvailableRoles = async () => {
    try {
      const rolesSnapshot = await getDocs(collection(db, 'advanced_roles'));
      const rolesData = rolesSnapshot.docs.map(doc => ({
        value: doc.id,
        label: doc.data().name || doc.id
      }));
      
      // Ajouter le rôle par défaut
      rolesData.push({ value: 'default', label: 'Par défaut' });
      
      setAvailableRoles(rolesData);
    } catch (error) {
      // Fallback aux rôles statiques en cas d'erreur
      setAvailableRoles([
        { value: 'super_admin', label: 'Super Administrateur' },
        { value: 'admin', label: 'Administrateur' },
        { value: 'coordinateur', label: 'Coordinateur' },
        { value: 'assistant_admin', label: 'Assistant Administratif' },
        { value: 'assistant_vie', label: 'Assistant de Vie' },
        { value: 'infirmier', label: 'Infirmier' },
        { value: 'default', label: 'Par défaut' }
      ]);
    }
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const templatesSnapshot = await getDocs(collection(db, 'dashboard_templates'));
      const templatesData = {};
      
      templatesSnapshot.forEach(doc => {
        templatesData[doc.id] = doc.data();
      });
      
      setTemplates(templatesData);
    } catch (error) {
      // Erreur silencieuse lors du chargement des templates
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (role) => {
    if (templates[role]) {
      setEditingTemplate({ ...templates[role], role });
    } else {
      // Nouveau template
      setEditingTemplate({
        role,
        name: `Dashboard ${availableRoles.find(r => r.value === role)?.label || role}`,
        description: '',
        tiles: Object.keys(allTiles).reduce((acc, tile) => {
          acc[tile] = false;
          return acc;
        }, {}),
        defaultView: 'dashboard'
      });
    }
  };

  const saveTemplate = async () => {
    if (!editingTemplate) return;
    
    try {
      const templateRef = doc(db, 'dashboard_templates', editingTemplate.role);
      await setDoc(templateRef, {
        ...editingTemplate,
        updatedAt: new Date()
      });
      
      await loadTemplates();
      setEditingTemplate(null);
    } catch (error) {
      // Erreur silencieuse lors de la sauvegarde du template
    }
  };

  const deleteTemplate = async (role) => {
    if (role === 'default') {
      alert('Le template par défaut ne peut pas être supprimé');
      return;
    }
    
    if (confirm(`Êtes-vous sûr de vouloir supprimer le template pour le rôle "${role}" ?`)) {
      try {
        await deleteDoc(doc(db, 'dashboard_templates', role));
        await loadTemplates();
      } catch (error) {
        // Erreur silencieuse lors de la suppression du template
      }
    }
  };

  const toggleTile = (tileKey) => {
    if (!editingTemplate) return;
    
    setEditingTemplate(prev => ({
      ...prev,
      tiles: {
        ...prev.tiles,
        [tileKey]: !prev.tiles[tileKey]
      }
    }));
  };

  const previewTemplateView = (template) => {
    setPreviewTemplate(template);
    setShowPreview(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Chargement des templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {/* En-tête */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
                <Settings className="text-blue-600 dark:text-blue-400" size={28} />
                Gestion des Templates de Dashboard
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Configurez les dashboards selon les rôles des utilisateurs
              </p>
            </div>
          </div>
        </div>

        {/* Liste des templates */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {availableRoles.map(role => {
              const template = templates[role.value];
              const isDefault = role.value === 'default';
              
              return (
                <div key={role.value} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-800 dark:text-gray-100">{role.label}</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() => previewTemplateView(template || { role: role.value, tiles: {} })}
                        className="p-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                        title="Aperçu"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => startEditing(role.value)}
                        className="p-1 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300"
                        title="Modifier"
                      >
                        <Edit size={16} />
                      </button>
                      {!isDefault && (
                        <button
                          onClick={() => deleteTemplate(role.value)}
                          className="p-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>

                  {template ? (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600 dark:text-gray-300">{template.description}</p>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Tuiles activées: {Object.values(template.tiles || {}).filter(Boolean).length} / {Object.keys(allTiles).length}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <button
                        onClick={() => startEditing(role.value)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm flex items-center gap-1 mx-auto"
                      >
                        <Plus size={16} />
                        Créer le template
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Modal d'édition */}
      {editingTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {templates[editingTemplate.role] ? 'Modifier' : 'Créer'} Template
                </h2>
                <button
                  onClick={() => setEditingTemplate(null)}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Rôle
                  </label>
                  <select
                    value={editingTemplate.role}
                    onChange={(e) => setEditingTemplate(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    disabled={!!templates[editingTemplate.role]}
                  >
                    {availableRoles.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nom du dashboard
                  </label>
                  <input
                    type="text"
                    value={editingTemplate.name}
                    onChange={(e) => setEditingTemplate(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Ex: Dashboard Administrateur"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={editingTemplate.description}
                    onChange={(e) => setEditingTemplate(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    rows="2"
                    placeholder="Description du template..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Tuiles à afficher
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto">
                    {Object.entries(allTiles).map(([key, label]) => (
                      <label key={key} className="flex items-center space-x-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded">
                        <input
                          type="checkbox"
                          checked={!!editingTemplate.tiles[key]}
                          onChange={() => toggleTile(key)}
                          className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setEditingTemplate(null)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md"
                >
                  Annuler
                </button>
                <button
                  onClick={saveTemplate}
                  className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center gap-2"
                >
                  <Save size={16} />
                  Sauvegarder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'aperçu */}
      {showPreview && previewTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  Aperçu - {previewTemplate.name || `Dashboard ${previewTemplate.role}`}
                </h2>
                <button
                  onClick={() => setShowPreview(false)}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="bg-gray-100 dark:bg-gray-700 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Tuiles activées:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(allTiles).map(([key, label]) => (
                    <div
                      key={key}
                      className={`p-3 rounded border ${
                        previewTemplate.tiles?.[key]
                          ? 'bg-green-50 dark:bg-green-900 border-green-200 dark:border-green-700 text-green-800 dark:text-green-200'
                          : 'bg-red-50 dark:bg-red-900 border-red-200 dark:border-red-700 text-red-800 dark:text-red-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{label}</span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          previewTemplate.tiles?.[key]
                            ? 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200'
                            : 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200'
                        }`}>
                          {previewTemplate.tiles?.[key] ? 'Activée' : 'Désactivée'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardTemplateManager;
